import axios from 'axios';
import { setListingResolver, setCriteriaResolver } from '@bay-email/core';
import { prepareSearchParams } from './criteriaSearchParams';

/**
 * Single seam between the listing blocks and whatever app is hosting them.
 *
 * The blocks used to call `/global/components/listings.cfc` directly from seven
 * places. That route only exists in ClientBAY — SMC and the Design app load the
 * same package and have no such endpoint, so listing search was hardwired to one
 * consumer from inside a shared library.
 *
 * Now every listing request goes through `fetchListings`, and a host app can
 * replace the transport at boot with `setListingsFetcher`. The default keeps the
 * old ClientBAY call verbatim, so a host that wires nothing behaves exactly as
 * before.
 *
 * ponytail: module-level singleton rather than React context — the callers
 * include plain .ts service modules with no provider in reach. Move it onto
 * EmailEditorAppConfig.services.listings once those are all components.
 */

export interface ListingsRequest {
	/** listings.cfc `method` — e.g. 'read', 'doCriteriaSearch'. May also be
	 *  supplied inside `params`, which is how most of the pickers build it. */
	method?: string;
	/** Query parameters, already shaped for the endpoint. */
	params?: Record<string, any>;
	/** Passed straight through to the transport for cancellation. */
	cancelToken?: any;
	signal?: AbortSignal;
}

/** Raw endpoint payload. Callers read STATUS/SEARCHLISTINGS/TOTALROWCOUNT off it. */
export interface ListingsResponse {
	STATUS: number;
	TOTALROWCOUNT?: number;
	SEARCHLISTINGS?: any[];
	MESSAGE?: string;
	[key: string]: any;
}

export type ListingsFetcher = (
	_request: ListingsRequest,
) => Promise<ListingsResponse>;

const defaultFetcher: ListingsFetcher = async ({
	method,
	params,
	cancelToken,
	signal,
}) => {
	const resp = await axios.get<ListingsResponse>(
		'/global/components/listings.cfc',
		{
			params: method ? { method, ...(params || {}) } : { ...(params || {}) },
			cancelToken,
			signal,
		},
	);

	return resp.data;
};

let fetcher: ListingsFetcher = defaultFetcher;

/**
 * Ids are composite — `ml_number|mlsid|statuscode|reduceddatetime` — which is
 * what the pickers build and what blocks persist. Only the first two segments
 * identify the property; the rest are snapshot fields that change over the
 * listing's life, so match on the stable prefix.
 */
function listingKeyPrefix(id: string): string {
	return id.split('|').slice(0, 2).join('|');
}

/**
 * Raw CFC row plus the display aliases every picker reads.
 *
 * `listings.cfc` speaks `streetaddress` / `ml_number` / `zip`; the pickers were
 * each written against `address` / `mls` / `postalCode` and built those aliases
 * inside their own row mappers, on the way out of a search. Nothing built them
 * on the way back IN — this resolver handed the cache the raw row — so a
 * listing looked complete right after you picked it and lost half its fields
 * the moment the design was reopened and rehydrated from its saved id.
 *
 * That is what made SingleListingPicker's `hasValidSelection` (which tests
 * `selectedListing.address`) go false on every reopened design: the binding
 * resolved, the canvas painted, and the drawer still offered "Select Listing"
 * because the one field it gates on only ever existed on search results.
 *
 * Raw fields are spread first and never overwritten by an alias — merge tags
 * resolve against the CFC names, and dropping any of them breaks every
 * `{{listing.*}}` outside the block that happened to be focused.
 *
 * ponytail: the four picker row-mappers still build these aliases themselves.
 * Point them here so search results and rehydrated rows cannot drift again.
 */
export function toDisplayListing(
	row: Record<string, any>,
	id: string,
): Record<string, any> {
	return {
		...row,
		id,
		title: row.title ?? row.streetaddress,
		address: row.address ?? row.streetaddress,
		city: row.city,
		stateProvince: row.stateProvince ?? row.state,
		postalCode: row.postalCode ?? row.zip ?? row.zip_code,
		mls: row.mls ?? row.ml_number,
		dateListed:
			row.dateListed ??
			row.listingDisplayDate ??
			row.LISTINGDISPLAYDATE ??
			row.AddDateTime ??
			row.adddatetime,
	};
}

/**
 * Look up listings by id for the core render cache. Returns a map keyed by the
 * id that was asked for; ids absent from the map are treated as unavailable and
 * render their fallback.
 *
 * FAILING TO MATCH HERE IS PERMANENT. An id this returns nothing for lands in
 * the cache's `unavailable` set, and `requestListings` never asks again for the
 * life of the page — `resolveBoundListing` returns null from then on, the
 * canvas paints its fallback, and the attributes drawer offers "Select
 * Listing". That is by design for a sold or deleted listing, but it makes any
 * matching defect below look identical to one, and look permanent:
 *
 *   - the search is `searchfield: 'ml_number'` with `MaxRows: ids.length`, so
 *     one requested id buys exactly one row. An ml_number that is not unique
 *     across MLSs can return the wrong MLS's row, which then fails the
 *     `ml_number|mlsid` prefix match below and takes the id down with it.
 *   - only the first two segments of the composite id are matched, so a row
 *     whose ml_number or mlsid casing/padding differs from what was persisted
 *     silently misses.
 *
 * If a listing that plainly exists renders as unavailable, suspect this
 * function before suspecting the binding.
 *
 * ponytail: no retry and no distinction between "gone" and "lookup missed";
 * give the resolver a not-found vs failed-match result if this bites.
 */
export async function resolveListingsByIds(
	ids: string[],
): Promise<Record<string, Record<string, any>>> {
	if (!ids.length) return {};

	const wantedByPrefix = new Map<string, string>();
	ids.forEach(id => wantedByPrefix.set(listingKeyPrefix(id), id));

	const data = await fetchListings({
		params: {
			method: 'read',
			CacheData: true,
			Return_Query: false,
			Return_Array: true,
			// ON, not off: this call re-hydrates a stored selectedListingId when a
			// design reopens, and its row becomes the block's listing. Without the
			// compose pass the reopened listing carried '' for agentname/agentname2/
			// listingstatus/listingurl, so those merge tags resolved only until the
			// first save/reopen cycle.
			doQueryFields: true,
			ReturnFormat: 'json',
			deleteflag: 'n',
			showallproperties: true,
			MaxRows: ids.length,
			startpage: 1,
			searchfield: 'ml_number',
			query: ids.map(id => id.split('|')[0]).filter(Boolean).join(','),
		},
	});

	if (data?.STATUS !== 0) return {};

	const rows: any[] = Array.isArray(data.QUERY)
		? data.QUERY
		: Array.isArray(data.SEARCHLISTINGS)
			? data.SEARCHLISTINGS
			: [];

	const resolved: Record<string, Record<string, any>> = {};

	rows.forEach(row => {
		const requestedId = wantedByPrefix.get(
			`${row?.ml_number ?? ''}|${row?.mlsid ?? ''}`,
		);
		if (!requestedId) return;
		resolved[requestedId] = toDisplayListing(row, requestedId);
	});

	return resolved;
}

/**
 * Install the host app's listing transport. Pass nothing (or null) to restore
 * the built-in ClientBAY call.
 */
export function setListingsFetcher(next?: ListingsFetcher | null): void {
	fetcher = next || defaultFetcher;
}

/**
 * `listings.cfc` returns `image_data` as a JSON *string* of
 * `{description, position, url}` records, but every consumer in this package —
 * block render, merge-tag resolution, the `image_data?: string[]` interfaces —
 * expects an array of URLs. Left encoded, every `Array.isArray(image_data)`
 * check fails and images fall through to the `nophoto` placeholder that
 * `read()` hard-codes into `imagepath`/`thumbpath`.
 *
 * ponytail: `custom_image_data` (an agent's own uploads, same shape) is ignored
 * — decode and prefer it here once the precedence rule is settled with the CFC.
 */
export function decodeImageData(raw: unknown): string[] | undefined {
	if (typeof raw === 'string') {
		if (raw.trim() === '') return undefined;
		try {
			return decodeImageData(JSON.parse(raw));
		} catch {
			// A malformed payload is a listing with no images, not a broken render.
			return undefined;
		}
	}

	const records = Array.isArray(raw)
		? raw
		: raw && typeof raw === 'object'
			? objectToRecords(raw as Record<string, any>)
			: undefined;

	if (!records) return undefined;

	return records
		.slice()
		.sort((a: any, b: any) => (field(a, 'position') ?? 0) - (field(b, 'position') ?? 0))
		.map((item: any) => (typeof item === 'string' ? item : field(item, 'url')))
		.filter(
			(url: unknown): url is string =>
				typeof url === 'string' && url.trim() !== '',
		);
}

/** ColdFusion casing is not guaranteed — `url` one call, `URL` the next. */
function field(item: any, name: string): any {
	return item?.[name] ?? item?.[name.toUpperCase()];
}

/**
 * The records arrive as an array most of the time, but not always. Each shape
 * below used to fail the old `Array.isArray` check and drop every image.
 */
function objectToRecords(raw: Record<string, any>): any[] {
	// A serialized ColdFusion query: parallel COLUMNS/DATA arrays.
	const columns = field(raw, 'columns');
	const rows = field(raw, 'data');
	if (Array.isArray(columns) && Array.isArray(rows)) {
		return rows.map((row: any) =>
			Object.fromEntries(
				columns.map((column: any, index: number) => [
					String(column).toLowerCase(),
					Array.isArray(row) ? row[index] : undefined,
				]),
			),
		);
	}

	// A single record, rather than a collection of them.
	if (typeof field(raw, 'url') === 'string') return [raw];

	// A struct keyed by position/id, whose values are the records.
	return Object.values(raw);
}

function decodeRowImages(row: any) {
	const decoded = decodeImageData(row?.image_data);
	return decoded ? { ...row, image_data: decoded } : row;
}

/**
 * Every listing request in the package goes through here — which makes it the
 * one place to normalize the wire format, so no caller has to know it.
 */
export async function fetchListings(
	request: ListingsRequest,
): Promise<ListingsResponse> {
	// listings.cfc keeps the listing detail columns off by default so general
	// search and the grid don't pay for Description (varchar(MAX)) on every row.
	// Every picker in this package feeds its rows to primeListings, so in here
	// they are always render data — ask for the columns unless a caller (the
	// count query) explicitly opts out.
	//
	// doQueryFields likewise: listings.cfc selects every office and agent field as
	// a literal '' and fills them in afterwards from an agents lookup, which only
	// runs when this is on. With it off, Office Name/Phone/Address/City/State/Zip/
	// Domain/Logo and Agent First/Last/Email/Website/Team/Certification all come
	// back as empty strings rather than missing — which is why they read as
	// correctly mapped and still rendered blank.
	//
	// doQueryFieldsForArray: the CFC historically skipped that compose pass for
	// Return_Array responses — which is every picker here — so agentname,
	// agentname2, listingstatus and listingurl stayed '' and the Listing Agent /
	// Listing Status / Listing URL merge tags never resolved. This opts the email
	// editor in; legacy Return_Array callers elsewhere keep the old shape.
	const data = await fetcher({
		...request,
		params: {
			IncludeDetailFields: true,
			doQueryFields: true,
			doQueryFieldsForArray: true,
			orderby: 'adddatetime',
			orderdirection: 'desc',
			...request.params,
		},
	});
	if (!data) return data;

	const decoded: ListingsResponse = { ...data };

	if (Array.isArray(decoded.SEARCHLISTINGS)) {
		decoded.SEARCHLISTINGS = decoded.SEARCHLISTINGS.map(decodeRowImages);
	}
	if (Array.isArray(decoded.QUERY)) {
		decoded.QUERY = decoded.QUERY.map(decodeRowImages);
	}

	return decoded;
}

/**
 * Run a criteria block's saved query. Mirrors the shape `listingsService`
 * sends for the drawer's count, so the canvas and the picker agree on what a
 * given set of criteria matches.
 */
export async function resolveListingsByCriteria(
	config: Record<string, any>,
): Promise<Array<Record<string, any>>> {
	const data = await fetchListings({
		// Same params the drawer's count sends, so canvas and drawer agree (monday 13121451389).
		params: { method: 'doCriteriaSearch', ...prepareSearchParams(config) },
	});

	if (data?.STATUS !== 0) return [];

	const rows: any[] = Array.isArray(data.SEARCHLISTINGS)
		? data.SEARCHLISTINGS
		: Array.isArray(data.QUERY)
			? data.QUERY
			: [];

	return rows.map(row => ({
		...row,
		id: `${row?.ml_number ?? ''}|${row?.mlsid ?? ''}|${row?.statuscode ?? ''}|${row?.reduceddatetime ?? ''}`,
	}));
}

/** Total matches for a criteria config - the same query the drawer's count runs. */
export async function countListingsByCriteria(config: Record<string, any>): Promise<number> {
	const data = await fetchListings({
		params: { method: 'doCriteriaSearch', ...prepareSearchParams(config), IncludeDetailFields: false },
	});
	return data?.STATUS === 0 ? Number(data.TOTALROWCOUNT) || 0 : 0;
}

// Core holds the render-time caches but owns no transport. Hand it both
// resolvers on import so blocks resolve their bindings without core learning
// anything about HTTP.
setListingResolver(resolveListingsByIds);
setCriteriaResolver(resolveListingsByCriteria);
